import { Controller } from "@hotwired/stimulus";
import Swiper from "swiper";
// eslint-disable-next-line import/no-unresolved
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import type { SwiperOptions, SwiperModule } from "swiper/types";

const ALLOWED_KEYS: (keyof SwiperOptions | "modules")[] = [
  "modules",
  "effect",
  "autoplay",
  "pagination",
  "fadeEffect",
  "speed",
];

const MODULES_MAP: Record<string, SwiperModule> = {
  Autoplay,
  EffectFade,
  Pagination,
};

type Module = keyof typeof MODULES_MAP;

type CustomOptions = {
  navigation: boolean;
};

type UserConfig = Partial<
  Omit<SwiperOptions, "modules"> & {
    modules?: Module[];
  }
>;
export default class extends Controller<HTMLElement> {
  static targets = ["carousel", "button", "playPauseText", "play", "pause"];

  static classes = ["active"];

  static values = {
    options: Object,
    customOptions: Object,
  };

  DEFAULT_ACTIVE_CLASS = "swiper-button-active";

  declare carouselTarget: HTMLElement;
  declare readonly playPauseTextTarget: HTMLButtonElement;
  declare readonly playTarget: SVGElement;
  declare readonly pauseTarget: SVGElement;
  declare swiper: Swiper;
  declare buttonTargets: Array<HTMLButtonElement>;
  declare optionsValue: UserConfig;
  declare activeClass: string;
  declare customOptionsValue: CustomOptions;

  connect(): void {
    try {
      this.init();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("Swiper config error:", error);
    }
  }

  init() {
    const config = this.buildConfig(this.optionsValue);
    this.swiper = new Swiper(this.carouselTarget, config);

    if (this.customOptionsValue.navigation) {
      this.swiper.on("transitionStart", () => {
        const current = document.querySelector(
          `[data-index="${this.swiper.realIndex}"]`
        );
        this.removeAllActiveClasses();
        current.classList.add(this.activeClass || this.DEFAULT_ACTIVE_CLASS);
      });
    }
  }

  disconnect(): void {
    this.swiper.off("transitionStart");
  }

  moveSlide(e: Event) {
    const target = e.currentTarget as HTMLElement;
    const index = Number(target.dataset.index);
    this.swiper.slideTo(index);
  }

  private buildConfig(userOptions: UserConfig): SwiperOptions {
    const config: SwiperOptions = {};

    for (const key in userOptions) {
      if (!ALLOWED_KEYS.includes(key as keyof SwiperOptions | "modules")) {
        throw new Error(`Unsupported Swiper option: "${key}"`);
      }
    }

    if (userOptions.modules) {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      config.modules = userOptions.modules
        .filter((name) => {
          // Remove Autoplay if user prefers reduced motion
          if (prefersReducedMotion && name === "Autoplay") {
            return false;
          }
          return true;
        })
        .map((name) => {
          const module = MODULES_MAP[name];
          if (!module) {
            throw new Error(`Unsupported Swiper module: "${name}"`);
          }
          return module;
        });
    }

    for (const key of ALLOWED_KEYS) {
      if (key !== "modules" && key in userOptions) {
        (config[key] as unknown) = userOptions[key as keyof SwiperOptions];
      }
    }

    return config;
  }

  togglePlayPause() {
    const isRunning = this.swiper.autoplay.running;

    this.playPauseTextTarget.textContent = isRunning ? "Play" : "Pause";
    this.playTarget.classList.toggle("d-none", !isRunning);
    this.pauseTarget.classList.toggle("d-none", isRunning);

    isRunning ? this.swiper.autoplay.stop() : this.swiper.autoplay.start();
  }

  private removeAllActiveClasses() {
    this.buttonTargets.forEach((button) =>
      button.classList.remove(this.activeClass || this.DEFAULT_ACTIVE_CLASS)
    );
  }
}
